/**
 * @fileoverview Affiliates tab of the billing admin (MOR-438)
 *
 * Affiliate agreements as cards: the declining rate schedule rendered as a
 * timeline, the gross-margin adjustment in words, and how many firms bill
 * under each agreement. Breakdown opens the month-by-month working.
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Card, CardContent } from "@/ui/primitives/card";
import { Skeleton } from "@/ui/primitives/skeleton";
import { Handshake, Pencil, Plus, TableProperties } from "lucide-react";
import { adjustmentExplainer } from "./affiliateCopy";
import type { Agreement } from "./billingFormat";
import { EmptyState, RateScheduleTimeline } from "./billingShared";

export function AffiliatesTab({
  onEdit,
  onBreakdown,
}: {
  onEdit: (agreement: Agreement | "new") => void;
  onBreakdown: (id: Id<"affiliateAgreements">) => void;
}) {
  const { data: overview, isPending } = useQuery(
    convexQuery(api.admin.billing.getBillingOverview, {}),
  );

  if (isPending || !overview) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10 rounded-lg" />
        <Skeleton className="h-[180px] rounded-xl" />
      </div>
    );
  }

  const active = overview.agreements.filter((a) => !a.archived);
  const archived = overview.agreements.filter((a) => a.archived);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Affiliate agreements</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Fee rates decline over time, measured from each firm's conversion
            date, with an optional gross-margin adjustment before the rate is
            applied.
          </p>
        </div>
        <Button onClick={() => onEdit("new")}>
          <Plus className="mr-1 h-4 w-4" />
          New agreement
        </Button>
      </div>

      {overview.agreements.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={<Handshake className="h-8 w-8" />}
              title="No affiliate agreements yet"
              hint="Create one to start tracking partner fees on converted firms."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {active.map((agreement) => (
            <AgreementCard
              key={agreement._id}
              agreement={agreement}
              onEdit={onEdit}
              onBreakdown={onBreakdown}
            />
          ))}
          {archived.length > 0 && (
            <>
              <h3 className="mt-2 text-sm font-medium text-muted-foreground">
                Archived
              </h3>
              {archived.map((agreement) => (
                <AgreementCard
                  key={agreement._id}
                  agreement={agreement}
                  onEdit={onEdit}
                  onBreakdown={onBreakdown}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function AgreementCard({
  agreement,
  onEdit,
  onBreakdown,
}: {
  agreement: Agreement;
  onEdit: (agreement: Agreement) => void;
  onBreakdown: (id: Id<"affiliateAgreements">) => void;
}) {
  return (
    <Card className={agreement.archived ? "opacity-60" : undefined}>
      <CardContent className="flex flex-col gap-5 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
              {agreement.name.slice(0, 1).toUpperCase()}
            </span>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold">
                  {agreement.name}
                </span>
                {agreement.archived && (
                  <Badge color="gray" variant="outline" size="sm">
                    Archived
                  </Badge>
                )}
              </div>
              {agreement.partnerEmail && (
                <div className="text-xs text-muted-foreground">
                  {agreement.partnerEmail}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onBreakdown(agreement._id)}
            >
              <TableProperties className="mr-1.5 h-3.5 w-3.5" />
              Breakdown
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onEdit(agreement)}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Edit
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_auto]">
          <div className="flex flex-col gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Rate schedule
              <span className="ml-1.5 normal-case tracking-normal">
                (months since conversion)
              </span>
            </span>
            <RateScheduleTimeline
              schedule={agreement.rateSchedule}
              className="max-w-xl"
            />
          </div>
          <div className="flex flex-col gap-2 lg:w-56 lg:border-l lg:pl-5">
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Linked firms
            </span>
            <span className="text-2xl font-semibold tabular-nums leading-none">
              {agreement.linkedOrgCount}
            </span>
          </div>
        </div>

        <div className="rounded-lg border bg-muted/30 px-3.5 py-2.5 text-xs leading-relaxed text-muted-foreground">
          {adjustmentExplainer(agreement.grossMarginAdjustment)}
        </div>

        {agreement.notes && (
          <p className="text-xs text-muted-foreground">{agreement.notes}</p>
        )}
      </CardContent>
    </Card>
  );
}
